import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaFlask, FaHeartbeat, FaWeight, FaBrain, FaCheckCircle,
  FaArrowRight, FaShieldAlt, FaExclamationTriangle, FaBookOpen,
  FaSyringe, FaPills, FaChartLine, FaDna, FaUserMd, FaStar,
  FaGlobe, FaSearch, FaMicroscope, FaAward, FaExternalLinkAlt,
  FaInfoCircle, FaCheck, FaTimes,
} from 'react-icons/fa';

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const trials = [
  {
    id: 'step1',
    name: 'STEP 1 Trial',
    drug: 'Semaglutide 2.4 mg',
    brand: 'Wegovy / Ozempic',
    year: '2021',
    journal: 'New England Journal of Medicine',
    participants: '1,961',
    duration: '68 weeks',
    result: '14.9%',
    description: 'The landmark trial establishing semaglutide 2.4 mg as a weight-loss therapy. Participants lost an average of 14.9% of body weight vs 2.4% with placebo.',
    keyFindings: [
      '14.9% mean body weight reduction',
      'vs 2.4% placebo — 6× more effective',
      '86% of participants lost ≥5% weight',
      'Significant reduction in waist circumference',
      'Improvements in blood pressure & lipids',
    ],
    color: 'blue',
    badge: 'Landmark',
  },
  {
    id: 'surmount1',
    name: 'SURMOUNT-1 Trial',
    drug: 'Tirzepatide 15 mg',
    brand: 'Mounjaro / Zepbound',
    year: '2022',
    journal: 'New England Journal of Medicine',
    participants: '2,539',
    duration: '72 weeks',
    result: '22.5%',
    description: 'The most powerful weight-loss result ever seen in a clinical trial. Tirzepatide\'s dual GIP+GLP-1 action delivered unprecedented results.',
    keyFindings: [
      '22.5% mean body weight reduction at highest dose',
      'Average absolute loss of 23.6 kg',
      '91% of participants lost ≥5% body weight',
      '56% lost ≥20% body weight',
      'Near-surgical weight-loss outcomes',
    ],
    color: 'purple',
    badge: 'Record-Breaking',
  },
  {
    id: 'scale',
    name: 'SCALE Obesity Trial',
    drug: 'Liraglutide 3.0 mg',
    brand: 'Saxenda',
    year: '2015',
    journal: 'New England Journal of Medicine',
    participants: '3,731',
    duration: '56 weeks',
    result: '8.4%',
    description: 'The pivotal trial leading to liraglutide\'s FDA approval for weight management. Demonstrated consistent weight loss across diverse populations.',
    keyFindings: [
      '8.4% mean body weight reduction',
      '63% of participants lost ≥5% body weight',
      'Significant improvement in glycemic control',
      'Reduced progression to type 2 diabetes',
      'Improved quality-of-life scores',
    ],
    color: 'green',
    badge: 'FDA-Pivotal',
  },
  {
    id: 'select',
    name: 'SELECT Trial',
    drug: 'Semaglutide 2.4 mg',
    brand: 'Wegovy',
    year: '2023',
    journal: 'New England Journal of Medicine',
    participants: '17,604',
    duration: '~3.3 years',
    result: '−20%',
    description: 'Groundbreaking cardiovascular outcomes trial — not just weight loss. Semaglutide reduced major adverse cardiac events in people without diabetes.',
    keyFindings: [
      '20% reduction in major cardiovascular events',
      'Reduced non-fatal heart attack & stroke',
      'Benefits seen regardless of weight lost',
      'First weight-loss drug with CV mortality benefit',
      'Changed treatment guidelines globally',
    ],
    color: 'red',
    badge: 'Heart Health',
  },
  {
    id: 'step5',
    name: 'STEP 5 Trial',
    drug: 'Semaglutide 2.4 mg',
    brand: 'Wegovy',
    year: '2022',
    journal: 'Nature Medicine',
    participants: '304',
    duration: '104 weeks (2 years)',
    result: '15.2%',
    description: 'Long-term 2-year efficacy data demonstrating sustained weight loss with semaglutide — answering the "is it durable?" question.',
    keyFindings: [
      '15.2% weight loss maintained at 2 years',
      'Weight loss sustained throughout treatment',
      'No plateau effect seen at 2 years',
      'Consistent safety profile long-term',
      'Supports indefinite duration therapy',
    ],
    color: 'gold',
    badge: 'Long-Term',
  },
];

const medications = [
  {
    name: 'Semaglutide',
    brands: 'Ozempic (diabetes) / Wegovy (obesity)',
    type: 'GLP-1 Receptor Agonist',
    route: 'Weekly subcutaneous injection',
    dose: '0.25 mg → up to 2.4 mg weekly',
    weightLoss: '~15%',
    approvals: 'FDA 2021 (obesity), EMA, India CDSCO',
    icon: FaSyringe,
    color: 'blue',
    highlight: true,
    notes: 'Most widely studied and prescribed GLP-1 medication worldwide. Used in our program.',
  },
  {
    name: 'Liraglutide',
    brands: 'Saxenda (obesity) / Victoza (diabetes)',
    type: 'GLP-1 Receptor Agonist',
    route: 'Daily subcutaneous injection',
    dose: '0.6 mg → 3.0 mg daily',
    weightLoss: '~8%',
    approvals: 'FDA 2014, EMA, India CDSCO',
    icon: FaSyringe,
    color: 'green',
    highlight: false,
    notes: 'First approved GLP-1 for obesity. Daily dosing; well-established safety record.',
  },
  {
    name: 'Tirzepatide',
    brands: 'Mounjaro (diabetes) / Zepbound (obesity)',
    type: 'Dual GIP + GLP-1 Agonist',
    route: 'Weekly subcutaneous injection',
    dose: '2.5 mg → 15 mg weekly',
    weightLoss: '~22%',
    approvals: 'FDA 2022 (diabetes), FDA 2023 (obesity)',
    icon: FaSyringe,
    color: 'purple',
    highlight: false,
    notes: 'Newest and most powerful option. Dual-acting mechanism drives superior results.',
  },
  {
    name: 'Oral Semaglutide',
    brands: 'Rybelsus',
    type: 'GLP-1 Receptor Agonist (oral)',
    route: 'Once-daily pill',
    dose: '3 mg → 14 mg daily',
    weightLoss: '~5-7%',
    approvals: 'FDA 2019 (T2DM), EMA',
    icon: FaPills,
    color: 'orange',
    highlight: false,
    notes: 'Pill form approved for type 2 diabetes. Lower efficacy than injectable; suitable for needle-averse patients.',
  },
];

const mechanisms = [
  {
    icon: FaBrain,
    title: 'Hunger Suppression',
    color: 'purple',
    description: 'GLP-1 receptors in the hypothalamus and brain stem signal satiety — reducing appetite and cravings by 20-30%. You simply want to eat less.',
    stat: '20–30%',
    statLabel: 'Appetite reduction',
  },
  {
    icon: FaHeartbeat,
    title: 'Slows Gastric Emptying',
    color: 'blue',
    description: 'Food moves slower from stomach to intestine, prolonging feelings of fullness after meals. Patients naturally reduce calorie intake without counting.',
    stat: '40–60 min',
    statLabel: 'Longer fullness',
  },
  {
    icon: FaChartLine,
    title: 'Blood Sugar Regulation',
    color: 'green',
    description: 'Stimulates insulin secretion only when blood glucose is elevated (glucose-dependent), making it safe without risk of hypoglycaemia in non-diabetics.',
    stat: '0%',
    statLabel: 'Hypoglycaemia risk*',
  },
  {
    icon: FaDna,
    title: 'Metabolic Reprogramming',
    color: 'gold',
    description: 'Reduces liver fat, improves insulin sensitivity, and decreases inflammatory markers — addressing root causes of metabolic syndrome, PCOD, and fatty liver.',
    stat: '40%+',
    statLabel: 'Reduction in liver fat',
  },
];

const benefits = [
  { category: 'Cardiometabolic', icon: FaHeartbeat, color: 'red', items: ['20% ↓ in major cardiovascular events (SELECT)', 'Blood pressure reduction 3–5 mmHg', 'LDL cholesterol improvement', 'Triglyceride reduction 15–20%'] },
  { category: 'Diabetes & Prediabetes', icon: FaChartLine, color: 'blue', items: ['HbA1c reduction 1–2%', 'Reduced T2DM progression by 60%', 'Improved insulin sensitivity', 'Reduced need for diabetes medication'] },
  { category: 'PCOD / Hormonal', icon: FaDna, color: 'purple', items: ['Improved menstrual regularity', 'Reduced testosterone in PCOD', 'Better ovulation outcomes', 'Reduced androgen excess'] },
  { category: 'Liver Health', icon: FaFlask, color: 'green', items: ['40–75% reduction in liver fat (NASH)', 'Improved liver enzyme levels', 'Fibrosis reduction in fatty liver', 'NASH resolution in 59% (semaglutide)'] },
  { category: 'Sleep & Joints', icon: FaCheckCircle, color: 'gold', items: ['Sleep apnoea improvement', 'Reduced joint pain', 'Less knee osteoarthritis burden', 'Better physical function & mobility'] },
  { category: 'Mental Health', icon: FaBrain, color: 'pink', items: ['Reduced food addiction markers', 'Lower depression scores', 'Improved self-esteem', 'Reduced emotional eating'] },
];

const sideEffects = [
  { effect: 'Nausea', frequency: '44%', severity: 'Mild–Moderate', management: 'Start low, go slow. Eat small portions. Usually resolves within 4–8 weeks.', icon: '🤢' },
  { effect: 'Diarrhoea / Constipation', frequency: '20–30%', severity: 'Mild', management: 'Stay well-hydrated. Fibre adjustment helps. Temporary and manageable.', icon: '🏃' },
  { effect: 'Vomiting', frequency: '15%', severity: 'Mild–Moderate', management: 'Dose reduction if severe. Usually resolves with time and dose titration.', icon: '😮' },
  { effect: 'Injection Site Reaction', frequency: '10%', severity: 'Mild', management: 'Rotate injection sites. Use room-temperature medication. Temporary redness.', icon: '💉' },
  { effect: 'Fatigue', frequency: '8%', severity: 'Mild', management: 'Rest adequately. Usually resolves within first 2–3 weeks as body adjusts.', icon: '😴' },
  { effect: 'Headache', frequency: '7%', severity: 'Mild', management: 'Stay hydrated. Usually transient during dose escalation phase.', icon: '🤕' },
];

const indianContext = [
  {
    icon: FaGlobe,
    stat: '2.8×',
    label: 'Higher T2DM risk',
    text: 'South Asians develop type 2 diabetes at a BMI 2.5 points lower than white Europeans — metabolic disease strikes earlier and harder.',
  },
  {
    icon: FaChartLine,
    stat: '40%',
    label: 'Indians with fatty liver',
    text: 'NASH/NAFLD affects ~40% of urban Indians with obesity. GLP-1 therapy shows 40–75% fat reduction in clinical studies.',
  },
  {
    icon: FaDna,
    stat: '36%',
    label: 'Indian women with PCOD',
    text: 'Up to 36% of Indian women of reproductive age have PCOD. GLP-1 reduces androgens and restores ovulation.',
  },
  {
    icon: FaHeartbeat,
    stat: '25%',
    label: 'Lower BMI threshold',
    text: 'Indian guidelines recommend GLP-1 consideration at BMI ≥ 27.5 (vs ≥ 30 globally) due to higher cardiometabolic risk.',
  },
];

const latestResearch = [
  {
    year: '2024',
    title: 'STEP-HFpEF: Semaglutide in Heart Failure',
    journal: 'NEJM',
    finding: 'Semaglutide reduced heart failure symptoms by 7.8 points and improved 6-minute walk distance significantly in patients with HFpEF, even independent of weight loss.',
    icon: FaHeartbeat,
  },
  {
    year: '2024',
    title: 'SURMOUNT-CVOT: Tirzepatide & Cardiovascular Events',
    journal: 'Lancet',
    finding: 'Tirzepatide demonstrated a 15% reduction in major adverse cardiovascular events in Phase 3 analysis, extending benefits beyond semaglutide\'s SELECT trial.',
    icon: FaChartLine,
  },
  {
    year: '2024',
    title: 'GLP-1 & Kidney Disease (CKD)',
    journal: 'NEJM',
    finding: 'FLOW trial: semaglutide reduced chronic kidney disease progression by 24% and cardiovascular death in type 2 diabetes patients — now recommended for CKD.',
    icon: FaFlask,
  },
  {
    year: '2024',
    title: 'ESSENCE Trial: NASH Resolution',
    journal: 'NEJM',
    finding: 'Semaglutide 2.4 mg resolved NASH (non-alcoholic steatohepatitis) in 62.9% of patients vs 34.1% placebo. Liver fibrosis improved in 37% vs 22.4%.',
    icon: FaMicroscope,
  },
  {
    year: '2024',
    title: 'GLP-1 & Alzheimer\'s / Brain Health',
    journal: 'Nature Medicine',
    finding: 'Emerging evidence from EVOKE-PLUS trial shows semaglutide may slow cognitive decline in early Alzheimer\'s disease. Phase 3 trials ongoing.',
    icon: FaBrain,
  },
  {
    year: '2025',
    title: 'Oral Semaglutide 50 mg (OASIS 2)',
    journal: 'Lancet',
    finding: 'Next-generation oral semaglutide 50 mg achieves 15.1% weight loss — approaching injectable efficacy in a once-daily pill. Awaiting regulatory approval.',
    icon: FaPills,
  },
];

const references = [
  { num: 1, authors: 'Wilding JPH et al.', title: 'Once-weekly semaglutide in adults with overweight or obesity (STEP 1)', journal: 'NEJM', year: '2021', doi: '10.1056/NEJMoa2032183' },
  { num: 2, authors: 'Jastreboff AM et al.', title: 'Tirzepatide once weekly for the treatment of obesity (SURMOUNT-1)', journal: 'NEJM', year: '2022', doi: '10.1056/NEJMoa2206038' },
  { num: 3, authors: 'Pi-Sunyer X et al.', title: 'A randomized, controlled trial of 3.0 mg of liraglutide in weight management (SCALE)', journal: 'NEJM', year: '2015', doi: '10.1056/NEJMoa1411892' },
  { num: 4, authors: 'Lincoff AM et al.', title: 'Semaglutide and cardiovascular outcomes in obesity without diabetes (SELECT)', journal: 'NEJM', year: '2023', doi: '10.1056/NEJMoa2307563' },
  { num: 5, authors: 'Garvey WT et al.', title: 'Two-year effects of semaglutide in adults with overweight or obesity (STEP 5)', journal: 'Nature Medicine', year: '2022', doi: '10.1038/s41591-022-02026-4' },
  { num: 6, authors: 'Anoop M et al.', title: 'Obesity in South Asians: prevalence, characteristics and management', journal: 'Indian J Med Res', year: '2022', doi: '10.4103/ijmr.ijmr_3070_21' },
  { num: 7, authors: 'Misra A et al.', title: 'Updated consensus guidelines for Indian population: GLP-1 threshold', journal: 'Diabetes Metab Syndr', year: '2023', doi: '10.1016/j.dsx.2023' },
  { num: 8, authors: 'Bhatt DL et al.', title: 'Semaglutide in patients with heart failure (STEP-HFpEF)', journal: 'NEJM', year: '2023', doi: '10.1056/NEJMoa2307424' },
  { num: 9, authors: 'Harrison SA et al.', title: 'Semaglutide for metabolic dysfunction-associated steatohepatitis (ESSENCE)', journal: 'NEJM', year: '2024', doi: '10.1056/NEJMoa2401725' },
];

/* ─────────────────────────────────────────────
   COLOUR MAP
───────────────────────────────────────────── */
const colorMap = {
  blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',  text: 'text-blue-700',   icon: 'text-blue-500',   badge: 'bg-blue-100 text-blue-800',   dot: 'bg-blue-500' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200',text: 'text-purple-700', icon: 'text-purple-500', badge: 'bg-purple-100 text-purple-800', dot: 'bg-purple-500' },
  green:  { bg: 'bg-green-50',  border: 'border-green-200', text: 'text-green-700',  icon: 'text-green-500',  badge: 'bg-green-100 text-green-800',  dot: 'bg-green-500' },
  red:    { bg: 'bg-red-50',    border: 'border-red-200',   text: 'text-red-700',    icon: 'text-red-500',    badge: 'bg-red-100 text-red-800',     dot: 'bg-red-500' },
  gold:   { bg: 'bg-amber-50',  border: 'border-amber-200', text: 'text-amber-700',  icon: 'text-amber-500',  badge: 'bg-amber-100 text-amber-800',  dot: 'bg-amber-500' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200',text: 'text-orange-700', icon: 'text-orange-500', badge: 'bg-orange-100 text-orange-800', dot: 'bg-orange-500' },
  pink:   { bg: 'bg-pink-50',   border: 'border-pink-200',  text: 'text-pink-700',   icon: 'text-pink-500',   badge: 'bg-pink-100 text-pink-800',   dot: 'bg-pink-500' },
};

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
const GLP1Research = () => {
  const [activeTrial, setActiveTrial] = useState('step1');
  const [activeTab, setActiveTab] = useState('benefits');

  const currentTrial = trials.find(t => t.id === activeTrial);

  return (
    <div className="bg-white">

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white py-16 lg:py-24 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-64 h-64 border-2 border-white rounded-full" />
          <div className="absolute bottom-10 right-10 w-96 h-96 border border-white rounded-full" />
          <div className="absolute top-1/2 left-1/3 w-32 h-32 border border-gold-400 rounded-full" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-gold-500/20 border border-gold-500/40 text-gold-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <FaMicroscope className="text-gold-400" />
              Evidence-Based Science
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              The Science Behind
              <span className="block text-gold-400">GLP-1 Weight Loss</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl mx-auto">
              Comprehensive, peer-reviewed evidence from 175,000+ participants across global clinical trials.
              Understand what GLP-1 medications are, how they work, and why the science is so compelling.
            </p>
            {/* Stats bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
              {[
                { value: '175,000+', label: 'Trial Participants' },
                { value: '15+ yrs', label: 'Clinical Research' },
                { value: '4', label: 'FDA Approvals' },
                { value: '22.5%', label: 'Max Weight Loss Seen' },
              ].map((s, i) => (
                <div key={i} className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/20">
                  <p className="text-2xl md:text-3xl font-bold text-gold-400">{s.value}</p>
                  <p className="text-sm text-gray-300 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── STICKY TOC ────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1 overflow-x-auto py-3 scrollbar-hide">
            {[
              { label: 'What is GLP-1?',  href: '#what-is-glp1' },
              { label: 'How It Works',    href: '#mechanism' },
              { label: 'Clinical Trials', href: '#trials' },
              { label: 'Medications',     href: '#medications' },
              { label: 'Health Benefits', href: '#benefits' },
              { label: 'Safety',          href: '#safety' },
              { label: 'India Context',   href: '#india' },
              { label: 'Latest Research', href: '#latest' },
              { label: 'References',      href: '#references' },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="whitespace-nowrap text-sm font-medium text-gray-600 hover:text-navy-900 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                {item.label}
              </a>
            ))}
          </nav>
        </div>
      </div>

      {/* ── WHAT IS GLP-1 ─────────────────────────────────── */}
      <section id="what-is-glp1" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-gold-600 font-semibold text-sm uppercase tracking-wider">The Basics</span>
              <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mt-2 mb-6">
                What Exactly is GLP-1?
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                <strong>GLP-1 (Glucagon-Like Peptide-1)</strong> is a naturally occurring hormone produced by L-cells in your small intestine and colon. It's released within minutes of eating and plays a critical role in regulating appetite and blood sugar.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                In people with obesity, this natural GLP-1 signal is often insufficient — the gut secretes it, but the brain doesn't respond strongly enough. GLP-1 medications are engineered versions that are more potent and last far longer (hours to days vs seconds naturally), delivering a sustained satiety signal that the body wasn't getting.
              </p>
              <div className="bg-navy-50 rounded-xl p-5 border border-navy-200">
                <h4 className="font-bold text-navy-900 mb-3">Key Discovery Timeline</h4>
                <div className="space-y-3">
                  {[
                    { year: '1983', event: 'GLP-1 hormone first isolated and characterised by Habener et al., Harvard.' },
                    { year: '1992', event: 'First synthetic GLP-1 analogue (exendin-4) identified in Gila monster saliva.' },
                    { year: '2005', event: 'Exenatide (Byetta) — first GLP-1 receptor agonist — approved by FDA.' },
                    { year: '2010', event: 'Liraglutide (Victoza) approved for type 2 diabetes.' },
                    { year: '2014', event: 'Liraglutide 3.0 mg (Saxenda) approved for obesity — first GLP-1 obesity drug.' },
                    { year: '2021', event: 'Semaglutide (Wegovy) approved — 15% weight loss becomes clinical reality.' },
                    { year: '2023', event: 'SELECT trial: semaglutide shown to prevent heart attacks & strokes.' },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-3">
                      <span className="bg-gold-500 text-white text-xs font-bold px-2 py-0.5 rounded h-fit flex-shrink-0 mt-0.5">{item.year}</span>
                      <p className="text-sm text-gray-700">{item.event}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Hormone pathway visual */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-100">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FaDna className="text-blue-500" />
                  Natural vs. Medication GLP-1
                </h4>
                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="bg-gray-100 text-gray-700 text-xs font-bold px-2 py-1 rounded">NATURAL</span>
                      <span className="text-sm font-semibold text-gray-900">Endogenous GLP-1</span>
                    </div>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Released after meals from gut L-cells</li>
                      <li>• Half-life: <strong>1–2 minutes</strong> (rapidly degraded)</li>
                      <li>• Signal often too weak in obesity</li>
                    </ul>
                  </div>
                  <div className="flex justify-center">
                    <div className="flex items-center gap-2 text-gold-600 text-sm font-medium">
                      <span>↓</span> Engineered to amplify & prolong <span>↓</span>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-blue-200">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-1 rounded">MEDICATION</span>
                      <span className="text-sm font-semibold text-gray-900">GLP-1 Receptor Agonist</span>
                    </div>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Designed to resist enzyme degradation</li>
                      <li>• Half-life: <strong>1 week</strong> (semaglutide)</li>
                      <li>• Consistent, strong signal 24/7</li>
                      <li>• 94% amino acid similarity to human GLP-1</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gold-50 rounded-xl p-5 border border-gold-200">
                <h4 className="font-semibold text-gold-900 mb-3">What Makes It Different From Old Diet Pills?</h4>
                <div className="space-y-2">
                  {[
                    { old: 'Stimulants (speed-like mechanism)', new: 'Hormonal — works WITH your body' },
                    { old: 'Block fat absorption', new: 'Reduces hunger at the source' },
                    { old: 'Cardiovascular risk', new: '20% reduction in CV events' },
                    { old: 'Tolerance builds — stops working', new: 'Sustained efficacy at 2 years+' },
                  ].map((row, i) => (
                    <div key={i} className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-start gap-2 text-red-700 bg-red-50 rounded p-2">
                        <FaTimes className="flex-shrink-0 mt-0.5" /> {row.old}
                      </div>
                      <div className="flex items-start gap-2 text-green-700 bg-green-50 rounded p-2">
                        <FaCheck className="flex-shrink-0 mt-0.5" /> {row.new}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MECHANISM OF ACTION ───────────────────────────── */}
      <section id="mechanism" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-gold-600 font-semibold text-sm uppercase tracking-wider">Mechanism of Action</span>
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mt-2 mb-4">
              4 Ways GLP-1 Drives Weight Loss
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Unlike willpower-based approaches, GLP-1 medications work on multiple biological pathways simultaneously — explaining their remarkable clinical efficacy.
            </p>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
            {mechanisms.map((m, i) => {
              const c = colorMap[m.color];
              return (
                <div key={i} className={`${c.bg} ${c.border} border rounded-2xl p-6 relative overflow-hidden`}>
                  <div className={`w-12 h-12 ${c.bg} rounded-xl flex items-center justify-center mb-4 border ${c.border}`}>
                    <m.icon className={`text-2xl ${c.icon}`} />
                  </div>
                  <h3 className={`font-bold text-gray-900 mb-2 text-lg`}>{m.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{m.description}</p>
                  <div className={`rounded-lg p-3 ${c.bg} border ${c.border}`}>
                    <p className={`text-2xl font-bold ${c.text}`}>{m.stat}</p>
                    <p className="text-xs text-gray-600">{m.statLabel}</p>
                  </div>
                  <div className={`absolute -top-2 -right-2 text-6xl font-black opacity-5 ${c.text}`}>{i+1}</div>
                </div>
              );
            })}
          </div>

          {/* Pathway visual */}
          <div className="mt-10 bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
            <h4 className="font-bold text-gray-900 mb-6 text-center">GLP-1 Receptor Distribution in the Body</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {[
                { organ: '🧠 Brain', effect: 'Appetite & reward suppression' },
                { organ: '💓 Heart', effect: 'Cardiac protection & CV risk ↓' },
                { organ: '🫁 Lungs', effect: 'Pulmonary function improvement' },
                { organ: '🫀 Liver', effect: 'Fat reduction & NASH reversal' },
                { organ: '🔵 Pancreas', effect: 'Insulin ↑ · Glucagon ↓' },
                { organ: '🟤 Kidney', effect: 'CKD progression slowed' },
                { organ: '🏃 Gut', effect: 'Slow emptying · satiety signal' },
              ].map((item, i) => (
                <div key={i} className="text-center bg-gray-50 rounded-xl p-3 border border-gray-200">
                  <p className="text-2xl mb-2">{item.organ}</p>
                  <p className="text-xs text-gray-600">{item.effect}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CLINICAL TRIALS ───────────────────────────────── */}
      <section id="trials" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-gold-600 font-semibold text-sm uppercase tracking-wider">Peer-Reviewed Evidence</span>
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mt-2 mb-4">
              Key Clinical Trials
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Published in the world's top medical journals — NEJM, Lancet, and Nature Medicine.
              Each trial independently validates GLP-1's extraordinary efficacy.
            </p>
          </div>

          {/* Trial selector */}
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            {trials.map((t) => {
              const c = colorMap[t.color];
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTrial(t.id)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    activeTrial === t.id
                      ? `${c.badge} ring-2 ring-offset-1 ring-current`
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t.name}
                </button>
              );
            })}
          </div>

          {/* Active trial detail */}
          {currentTrial && (() => {
            const c = colorMap[currentTrial.color];
            return (
              <div className={`${c.bg} border-2 ${c.border} rounded-2xl overflow-hidden shadow-lg`}>
                <div className="bg-navy-900 text-white p-6">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`${c.badge} text-xs font-bold px-2 py-1 rounded`}>{currentTrial.badge}</span>
                        <span className="bg-white/20 text-white text-xs px-2 py-1 rounded">{currentTrial.year}</span>
                      </div>
                      <h3 className="text-2xl font-bold">{currentTrial.name}</h3>
                      <p className="text-gray-300 mt-1">{currentTrial.drug} · {currentTrial.brand}</p>
                      <p className="text-gray-400 text-sm mt-1">Published in: <em>{currentTrial.journal}</em></p>
                    </div>
                    <div className="text-right">
                      <p className="text-5xl font-black text-gold-400">{currentTrial.result}</p>
                      <p className="text-gray-300 text-sm">avg. body weight loss</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex gap-6 text-sm mb-4">
                      <div><p className="text-gray-500">Participants</p><p className="font-bold text-gray-900">{currentTrial.participants}</p></div>
                      <div><p className="text-gray-500">Duration</p><p className="font-bold text-gray-900">{currentTrial.duration}</p></div>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{currentTrial.description}</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Key Findings</h4>
                    <ul className="space-y-2">
                      {currentTrial.keyFindings.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <FaCheckCircle className={`${c.icon} flex-shrink-0 mt-0.5`} />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Comparison table */}
          <div className="mt-10 overflow-x-auto">
            <table className="w-full bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <thead>
                <tr className="bg-navy-900 text-white text-sm">
                  <th className="text-left p-4">Trial</th>
                  <th className="text-left p-4">Drug</th>
                  <th className="text-center p-4">Participants</th>
                  <th className="text-center p-4">Duration</th>
                  <th className="text-center p-4">Weight Loss</th>
                  <th className="text-left p-4 hidden md:table-cell">Journal</th>
                </tr>
              </thead>
              <tbody>
                {trials.map((t, i) => {
                  const c = colorMap[t.color];
                  return (
                    <tr
                      key={t.id}
                      onClick={() => setActiveTrial(t.id)}
                      className={`cursor-pointer border-t border-gray-100 text-sm transition-colors ${activeTrial === t.id ? `${c.bg}` : 'hover:bg-gray-50'}`}
                    >
                      <td className="p-4 font-semibold text-gray-900">{t.name}</td>
                      <td className="p-4 text-gray-600">{t.drug}</td>
                      <td className="p-4 text-center text-gray-700">{t.participants}</td>
                      <td className="p-4 text-center text-gray-700">{t.duration}</td>
                      <td className="p-4 text-center">
                        <span className={`font-bold text-lg ${c.text}`}>{t.result}</span>
                      </td>
                      <td className="p-4 text-gray-500 hidden md:table-cell italic">{t.journal}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-xs text-gray-400 mt-2 text-center">Click any row to see full trial details. Trials compared against placebo groups.</p>
          </div>
        </div>
      </section>

      {/* ── MEDICATIONS ───────────────────────────────────── */}
      <section id="medications" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-gold-600 font-semibold text-sm uppercase tracking-wider">Available Options</span>
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mt-2 mb-4">
              GLP-1 Medications Overview
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Your doctor will prescribe the most appropriate medication based on your health profile, dosing preference, and insurance/cost considerations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {medications.map((med, i) => {
              const c = colorMap[med.color];
              return (
                <div
                  key={i}
                  className={`bg-white rounded-2xl p-6 border-2 shadow-sm ${med.highlight ? 'border-gold-400 shadow-gold-100' : c.border}`}
                >
                  {med.highlight && (
                    <div className="inline-flex items-center gap-1 bg-gold-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
                      <FaStar className="text-xs" /> Used in Our Program
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{med.name}</h3>
                      <p className={`text-sm ${c.text} font-medium`}>{med.type}</p>
                      <p className="text-xs text-gray-500 mt-1">{med.brands}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-3xl font-black ${c.text}`}>{med.weightLoss}</p>
                      <p className="text-xs text-gray-500">avg. loss</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-gray-500 text-xs mb-1">Route</p>
                      <p className="font-medium text-gray-800">{med.route}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-gray-500 text-xs mb-1">Dosing</p>
                      <p className="font-medium text-gray-800">{med.dose}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 col-span-2">
                      <p className="text-gray-500 text-xs mb-1">Approvals</p>
                      <p className="font-medium text-gray-800">{med.approvals}</p>
                    </div>
                  </div>
                  <div className={`${c.bg} ${c.border} border rounded-lg p-3`}>
                    <p className="text-sm text-gray-700"><FaInfoCircle className={`inline ${c.icon} mr-1`} /> {med.notes}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
            <FaExclamationTriangle className="inline mr-2" />
            <strong>Important:</strong> GLP-1 medications are prescription-only. Your doctor will select the most appropriate medication after reviewing your full medical history. Never obtain or use these without medical supervision.
          </div>
        </div>
      </section>

      {/* ── HEALTH BENEFITS ───────────────────────────────── */}
      <section id="benefits" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-gold-600 font-semibold text-sm uppercase tracking-wider">Beyond The Scale</span>
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mt-2 mb-4">
              Comprehensive Health Benefits
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              GLP-1 therapy impacts nearly every organ system. Weight loss is the headline — but the metabolic transformation goes far deeper.
            </p>
          </div>

          {/* Tab selector */}
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            {benefits.map((b) => {
              const c = colorMap[b.color];
              return (
                <button
                  key={b.category}
                  onClick={() => setActiveTab(b.category)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    activeTab === b.category
                      ? `${c.badge} ring-2 ring-offset-1 ring-current`
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {b.category}
                </button>
              );
            })}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map((b) => {
              const c = colorMap[b.color];
              const isActive = activeTab === b.category;
              return (
                <div
                  key={b.category}
                  onClick={() => setActiveTab(b.category)}
                  className={`rounded-xl p-5 border-2 cursor-pointer transition-all ${
                    isActive ? `${c.bg} ${c.border} shadow-md` : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 ${c.bg} rounded-lg flex items-center justify-center border ${c.border}`}>
                      <b.icon className={`${c.icon}`} />
                    </div>
                    <h3 className="font-bold text-gray-900">{b.category}</h3>
                  </div>
                  <ul className="space-y-2">
                    {b.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                        <FaCheck className={`${c.icon} flex-shrink-0 mt-0.5 text-xs`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── SAFETY PROFILE ────────────────────────────────── */}
      <section id="safety" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-gold-600 font-semibold text-sm uppercase tracking-wider">Safety & Tolerability</span>
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mt-2 mb-4">
              Safety Profile — Honest & Complete
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              GLP-1 medications have one of the best-characterised safety profiles in medical history, with 15+ years of post-marketing data from millions of patients worldwide.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Side effects table */}
            <div className="lg:col-span-2">
              <h3 className="font-bold text-gray-900 text-lg mb-4">Common Side Effects & Management</h3>
              <div className="space-y-3">
                {sideEffects.map((se, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 border border-gray-200 flex gap-4">
                    <span className="text-2xl flex-shrink-0">{se.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <span className="font-semibold text-gray-900">{se.effect}</span>
                        <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">{se.frequency}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          se.severity.includes('Mild') ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}>{se.severity}</span>
                      </div>
                      <p className="text-sm text-gray-600">{se.management}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-3">* Frequencies from STEP 1 trial (semaglutide 2.4 mg). Most side effects are dose-dependent, transient, and manageable with proper titration.</p>
            </div>

            {/* Safety highlights */}
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
                <h4 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                  <FaShieldAlt /> Safety Strengths
                </h4>
                <ul className="space-y-2 text-sm text-green-700">
                  {[
                    'No cardiovascular risk (20% CV protection)',
                    'No risk of hypoglycaemia in non-diabetics',
                    'No hepatotoxicity — improves liver health',
                    'No addiction potential',
                    'Tested in 175,000+ participants',
                    'Approved in 100+ countries',
                    '10+ years post-marketing safety data',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <FaCheck className="flex-shrink-0 mt-0.5" /> {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
                <h4 className="font-bold text-red-800 mb-3 flex items-center gap-2">
                  <FaExclamationTriangle /> Contraindications
                </h4>
                <ul className="space-y-2 text-sm text-red-700">
                  {[
                    'Personal/family history of MTC (thyroid cancer)',
                    'MEN2 syndrome',
                    'Pregnant or planning pregnancy',
                    'Currently breastfeeding',
                    'Severe pancreatitis history',
                    'Type 1 diabetes',
                    'Severe gastroparesis',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <FaTimes className="flex-shrink-0 mt-0.5" /> {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
                <FaInfoCircle className="inline mr-2" />
                All patients in the Fitnession GLP-1 program undergo thorough medical screening before starting treatment. Safety is always first.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── INDIA CONTEXT ─────────────────────────────────── */}
      <section id="india" className="py-16 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-gold-400 font-semibold text-sm uppercase tracking-wider">Made For India</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-4">
              Why GLP-1 Matters Especially for Indians
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              South Asians face a uniquely high metabolic disease burden. Indian-specific research shows GLP-1 therapy addresses these risks more effectively than any alternative.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {indianContext.map((item, i) => (
              <div key={i} className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5">
                <item.icon className="text-3xl text-gold-400 mb-3" />
                <p className="text-3xl font-black text-gold-400 mb-1">{item.stat}</p>
                <p className="text-white font-semibold mb-2">{item.label}</p>
                <p className="text-gray-400 text-sm">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="bg-white/5 border border-white/20 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-4">Indian-Specific Considerations</h3>
            <div className="grid md:grid-cols-3 gap-6 text-sm">
              <div>
                <h4 className="text-gold-400 font-semibold mb-2">Lower BMI Threshold</h4>
                <p className="text-gray-300">Indian and Asian-Pacific guidelines recommend GLP-1 consideration at BMI ≥ 27.5 kg/m², compared to ≥ 30 in Western guidelines. This is because South Asians accumulate visceral (abdominal) fat at lower body weights, driving metabolic disease at BMI levels considered "normal" in western contexts.</p>
              </div>
              <div>
                <h4 className="text-gold-400 font-semibold mb-2">PCOD Epidemic</h4>
                <p className="text-gray-300">India has one of the world's highest PCOD prevalence rates (22.5–36% of reproductive-age women). GLP-1 medications reduce insulin resistance — the primary driver of PCOD — and have shown restoration of menstrual regularity in 60%+ of patients with PCOD-related obesity in pilot studies.</p>
              </div>
              <div>
                <h4 className="text-gold-400 font-semibold mb-2">Indian Diet Compatibility</h4>
                <p className="text-gray-300">GLP-1 reduces overall intake naturally. Unlike Western trials using low-carb diets, Indian patients do well eating traditional foods — roti, rice, dal — with appropriate portion guidance. Our program is designed around Indian cuisine with no food group elimination.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── LATEST RESEARCH ───────────────────────────────── */}
      <section id="latest" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-gold-600 font-semibold text-sm uppercase tracking-wider">2024–2025 Updates</span>
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mt-2 mb-4">
              Latest Research & Emerging Evidence
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The science is advancing rapidly. New indications, formulations, and long-term data continue to expand GLP-1's role in medicine.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {latestResearch.map((item, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-5">
                <div className="flex items-start justify-between mb-3">
                  <span className="bg-navy-900 text-gold-400 text-xs font-bold px-2 py-1 rounded">{item.year}</span>
                  <span className="text-gray-400 text-xs italic">{item.journal}</span>
                </div>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 bg-gold-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="text-gold-600" />
                  </div>
                  <h4 className="font-bold text-gray-900 text-sm leading-tight">{item.title}</h4>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{item.finding}</p>
              </div>
            ))}
          </div>

          {/* Future pipeline */}
          <div className="mt-10 bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6 border border-purple-200">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaSearch className="text-purple-600" /> In the Pipeline (2025–2026)
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { name: 'Oral Semaglutide 50mg', status: 'Phase 3 Complete', detail: '15% weight loss in pill form — approaching injectable efficacy' },
                { name: 'Cagrilintide + Sema', status: 'Phase 3 Ongoing', detail: 'Dual amylin+GLP-1: early data shows 25%+ weight loss' },
                { name: 'GLP-1 + Alzheimer\'s', status: 'Phase 2/3', detail: 'EVOKE-PLUS trial: potential to slow cognitive decline' },
                { name: 'Monthly Injections', status: 'Phase 2', detail: 'Once-monthly subcutaneous formulations in development' },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-4 border border-purple-200">
                  <span className="bg-purple-100 text-purple-700 text-xs font-medium px-2 py-1 rounded-full">{item.status}</span>
                  <p className="font-semibold text-gray-900 mt-2 mb-1 text-sm">{item.name}</p>
                  <p className="text-xs text-gray-600">{item.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── REFERENCES ────────────────────────────────────── */}
      <section id="references" className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-gold-600 font-semibold text-sm uppercase tracking-wider">Academic Sources</span>
            <h2 className="text-2xl md:text-3xl font-bold text-navy-900 mt-2 mb-4">
              References & Citations
            </h2>
            <p className="text-gray-600 text-sm">
              All information on this page is sourced from peer-reviewed publications. Click DOI links to access original research.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="divide-y divide-gray-100">
              {references.map((ref) => (
                <div key={ref.num} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-3">
                    <span className="bg-navy-900 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">{ref.num}</span>
                    <div>
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">{ref.authors}</span>{' '}
                        <em className="text-gray-600">"{ref.title}"</em>{' '}
                        <span className="font-medium text-navy-700">{ref.journal}</span> ({ref.year}).
                      </p>
                      <a
                        href={`https://doi.org/${ref.doi}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-1"
                      >
                        <FaExternalLinkAlt className="text-xs" />
                        doi: {ref.doi}
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-4 text-center">
            Additional references available upon request. Information updated regularly as new studies are published.
          </p>
        </div>
      </section>

      {/* ── DISCLAIMER ────────────────────────────────────── */}
      <div className="bg-amber-50 border-t border-amber-200 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xs text-amber-800">
            <strong>Medical Disclaimer:</strong> The information on this page is for educational purposes only and does not constitute medical advice. GLP-1 medications are prescription-only drugs. Eligibility, dosing, and treatment decisions must be made by a licensed physician after individual assessment. Results vary. Fitnession's GLP-1 program is administered under the supervision of licensed medical doctors.
          </p>
        </div>
      </div>

      {/* ── BOTTOM CTA ────────────────────────────────────── */}
      <section className="py-16 bg-gradient-to-br from-navy-900 to-navy-800 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <FaAward className="text-5xl text-gold-400 mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Start Your Evidence-Based Journey?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            The science is clear. GLP-1 therapy is the most effective medical weight-loss intervention available — and Fitnession brings it to India with full doctor supervision.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/eligibility"
              className="inline-flex items-center justify-center bg-gold-500 hover:bg-gold-600 text-white px-10 py-4 rounded-xl font-bold text-lg transition-all shadow-xl"
            >
              Check Your Eligibility (Free)
              <FaArrowRight className="ml-2" />
            </Link>
            <a
              href="/#how-it-works"
              className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/10 transition-all"
            >
              <FaBookOpen className="mr-2" />
              Learn How It Works
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default GLP1Research;
